import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const skillCategories = [
    {
      title: 'Software Architecture & Development',
      skills: [
        { name: 'Python', description: 'Advanced backend development and automation' },
        { name: 'C/C++', description: 'Embedded systems and performance-critical applications' },
        { name: 'JavaScript/Node.js', description: 'Full-stack web development and APIs' },
        { name: 'React', description: 'Modern frontend interfaces and user experiences' },
        { name: 'Microservices Architecture', description: 'Scalable distributed system design' },
      ]
    },
    {
      title: 'Infrastructure & Cloud Technologies',
      skills: [
        { name: 'AWS EC2', description: 'Cloud infrastructure deployment and management' },
        { name: 'Auto-scaling', description: 'Dynamic resource allocation and optimization' },
        { name: 'CI/CD Pipelines', description: 'Automated deployment and integration workflows' },
        { name: 'Docker/Kubernetes', description: 'Containerization and orchestration' },
        { name: 'Load Balancing', description: 'High-availability system architecture' },
      ]
    },
    {
      title: 'Specialized Domain Expertise',
      skills: [
        { name: 'Streaming Media/OTT', description: 'Video streaming platforms and content delivery' },
        { name: 'Building Automation', description: 'Smart building systems and energy optimization' },
        { name: 'Radiation Detection', description: 'Nuclear safety and monitoring systems' },
        { name: 'Industrial Systems', description: 'Manufacturing and process control automation' },
        { name: 'IoT Sensor Networks', description: 'Connected device ecosystems and data collection' },
      ]
    },
    {
      title: 'Hardware Integration',
      skills: [
        { name: 'Embedded Systems', description: 'Real-time control systems and firmware' },
        { name: 'PLC Programming', description: 'Industrial automation and control logic' },
        { name: 'Communication Protocols', description: 'Device interconnection and data exchange' },
        { name: 'Modbus/BACnet', description: 'Industrial and building automation protocols' },
        { name: 'Sensor Integration', description: 'Hardware sensor interfacing and calibration' },
      ]
    },
    {
      title: 'Database & Analytics',
      skills: [
        { name: 'Time-series Databases', description: 'Real-time data storage and analysis' },
        { name: 'SQL/NoSQL', description: 'Relational and document database systems' },
        { name: 'Data Analytics', description: 'Business intelligence and insights generation' },
        { name: 'Real-time Processing', description: 'Stream processing and live data analysis' },
        { name: 'Machine Learning', description: 'Predictive analytics and intelligent systems' },
      ]
    },
    {
      title: 'Enterprise Platforms',
      skills: [
        { name: 'RESTful APIs', description: 'Scalable web service architecture' },
        { name: 'Cloud-Native Solutions', description: 'Modern distributed application design' },
        { name: 'Performance Optimization', description: 'System efficiency and scalability enhancement' },
        { name: 'Security Architecture', description: 'Enterprise-grade security implementation' },
        { name: 'Regulatory Compliance', description: 'Industry standards and certification management' },
      ]
    },
  ];

  const tools = [
    'Django', 'FastAPI', 'HomeAssistant', 'Dell OEM Hardware', 'MQTT', 'TCP/IP',
    'Multicast Tech', 'CDN Implementation', 'Time-series DBs', 'Real-time Analytics',
    'WebRTC', 'WebSocket', 'Satellite Processing', 'PID Controllers', 'Spectroscopy', 'NRC/IAEA Compliance'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };


  return (
    <section id="skills" className="section-padding bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Skills & <span className="gradient-text">Expertise</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              13+ years of progressive experience across enterprise software architecture, embedded systems, and specialized industrial applications. 
              From IoT platforms to nuclear safety systems, mastering diverse technology domains.
            </p>
          </motion.div>

          {/* Skills Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {skillCategories.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                variants={itemVariants}
                className="card p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {category.title}
                </h3>
                <div className="space-y-4">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.div 
                      key={skillIndex}
                      className="border-l-4 border-primary-500 pl-4 py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + skillIndex * 0.1 }}
                      whileHover={{ scale: 1.02, x: 4 }}
                    >
                      <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                        {skill.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {skill.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tools & Technologies */}
          <motion.div variants={itemVariants} className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
              Tools & Technologies
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {tools.map((tool, index) => (
                <motion.span
                  key={index}
                  className="px-4 py-2 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded-full shadow-sm border border-gray-200 dark:border-gray-700 text-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.05 }}
                  whileHover={{ scale: 1.1, y: -2 }}
                >
                  {tool}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Experience Timeline */}
          <motion.div variants={itemVariants} className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
              Professional Journey
            </h3>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-0.5 h-full bg-primary-500"></div>
                
                {/* Timeline Items */}
                {[
                  {
                    year: '2019 - Present',
                    title: 'Chief Technology Officer',
                    company: 'Elementz Engineers Guild Pvt Ltd',
                    description: 'Directing technology vision and architecture. Leading 12+ engineers across specialized domains. Delivered 6 major platform deployments.',
                    side: 'left'
                  },
                  {
                    year: '2016 - 2018',
                    title: 'Product Architect',
                    company: 'Elementz Engineers Guild Pvt Ltd',
                    description: 'Designed scalable platforms supporting 5,000+ concurrent users. Achieved 99.9% uptime and 35-45% efficiency improvements.',
                    side: 'right'
                  },
                  {
                    year: '2013 - 2015',
                    title: 'Project Leader',
                    company: 'Elementz Engineers Guild Pvt Ltd',
                    description: 'Led organizational transformation from embedded hardware to comprehensive software solutions. Established Python as core platform.',
                    side: 'left'
                  },
                  {
                    year: '2011 - 2013',
                    title: 'Embedded Software Engineer',
                    company: 'CDAC, India',
                    description: 'Developed expertise in real-time embedded systems and industrial control applications. Advanced embedded system research.',
                    side: 'right'
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className={`relative flex items-center mb-8 ${
                      item.side === 'right' ? 'md:flex-row-reverse' : ''
                    }`}
                    initial={{ opacity: 0, x: item.side === 'right' ? 50 : -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1 + index * 0.2 }}
                  >
                    {/* Timeline Node */}
                    <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-3 h-3 bg-primary-500 rounded-full border-4 border-white dark:border-gray-800 z-10"></div>
                    
                    {/* Content */}
                    <div className={`ml-12 md:ml-0 md:w-5/12 ${
                      item.side === 'right' ? 'md:mr-12' : 'md:ml-12'
                    }`}>
                      <div className="card p-6">
                        <div className="text-primary-600 dark:text-primary-400 font-semibold text-sm mb-2">
                          {item.year}
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                          {item.title}
                        </h4>
                        <p className="text-primary-600 dark:text-primary-400 font-medium mb-3">
                          {item.company}
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Skills Summary */}
          <motion.div
            variants={itemVariants}
            className="text-center bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-4">
              Ready to Leverage My Expertise?
            </h3>
            <p className="text-lg mb-6 text-primary-100">
              13+ years of proven experience across diverse technology domains. 
              From streaming platforms to nuclear safety systems - comprehensive expertise for transformative solutions.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">6</div>
                <div className="text-primary-100">Specialized Domains</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">75+</div>
                <div className="text-primary-100">Major Installations</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">99.9%</div>
                <div className="text-primary-100">System Uptime Achieved</div>
              </div>
            </div>
            <motion.button
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Start Your Project
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;